import React from 'react';

import { SelectionSetting, SelectionSettingProps } from '@lumapps/designer-widget-settings/components/SelectionSetting';
import { SelectionSettingType } from '@lumapps/designer-widget-settings/types';
import { TrainingPickerDialog } from '@lumapps/learning-pickers/components/TrainingPickerDialog';
import { TRAINING_ENTRY } from '@lumapps/learning-pickers/keys';
import { TrainingPickerDialogProps } from '@lumapps/learning-pickers/types';
import { getTrainingsById, normalizeTraining } from '@lumapps/learning-trainings/api';
import { TrainingLegacy } from '@lumapps/learning/types';
import { learningHatImage } from '@lumapps/lumx/custom-icons';
import { mdiSchool } from '@lumapps/lumx/icons';
import { Text } from '@lumapps/lumx/react';
import { makeSecuredMediaURLRelative } from '@lumapps/medias/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { DATA_SCOPE } from '../../../constants';
import { WIDGET_TRAINING_LIST } from '../../../keys';

export interface SelectionSectionProps {
    currentSelection?: SelectionSettingType;
    onChange: (properties: { selection: SelectionSettingType }) => void;
}

/**
 * TrainingPickerDialog normalized for use 'id' instead of 'identifier' for SelectionSetting pattern.
 */
const TrainingPickerDialogWrapper: React.FC<
    TrainingPickerDialogProps & { onConfirm: (items: any[]) => void; initSelectedItems?: any[] }
> = ({ onConfirm, initSelectedItems, ...props }) => {
    const handleConfirm = React.useCallback(
        (items: TrainingLegacy[]) => onConfirm(items.map(normalizeTraining)),
        [onConfirm],
    );

    const normalizedInitSelectedItems = React.useMemo(
        () => initSelectedItems?.map((item: TrainingLegacy) => (item.identifier ? item : normalizeTraining(item))),
        [initSelectedItems],
    );

    return (
        <TrainingPickerDialog {...props} initSelectedItems={normalizedInitSelectedItems} onConfirm={handleConfirm} />
    );
};

export const SelectionSection: React.FC<SelectionSectionProps> = ({ currentSelection, onChange }) => {
    const { translateKey } = useTranslate();

    const entityBlock = React.useMemo(
        (): SelectionSettingProps<TrainingLegacy, any>['entityBlock'] => ({
            title: (training) => training.title,
            // eslint-disable-next-line react/no-unstable-nested-components
            subtitle: (training) => (
                <Text as="p" typography="body1" color="dark-L2" truncate>
                    {training.categoriesTitles[0]}
                </Text>
            ),
            icon: mdiSchool,
            image: (training) => makeSecuredMediaURLRelative(training.cover),
            fallback: learningHatImage,
        }),
        [],
    );

    const handleOnChange = React.useCallback(
        (selection: SelectionSettingType) => {
            onChange({ selection });
        },
        [onChange],
    );

    return (
        <SelectionSetting<TrainingLegacy, TrainingPickerDialogProps>
            entityBlock={entityBlock}
            entityPicker={{
                component: TrainingPickerDialogWrapper,
                props: {
                    isMultipleSelect: true,
                    selectedItemsLabelKey: GLOBAL.SELECTED_ITEMS_COUNT,
                },
            }}
            manualButtonText={TRAINING_ENTRY.PICKER_TITLE}
            onChange={handleOnChange}
            getEntities={getTrainingsById}
            currentSelection={currentSelection}
            dataScope={`${DATA_SCOPE}-selection`}
            manualOnly
            title={translateKey(WIDGET_TRAINING_LIST.SECTION_TITLE)}
        />
    );
};
